"use client"

import { authFetch } from '@/lib/auth-fetch'

import { Activity, ArrowUpDown, ChevronUp, ChevronDown, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { useEffect, useState, useMemo, useCallback } from "react"
import { useRouter } from "next/navigation"
import NumberFlow from "@number-flow/react"
import { useCryptoWebSocket } from "@/hooks/use-crypto-websocket"
import { useCommandStore } from "@/store/command-store"
import { useTokenIcons } from "@/hooks/use-token-icons"
import { motion } from "motion/react"
import { createPortal } from "react-dom"
import {
  DndContext,
  useDraggable,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core"
import { restrictToWindowEdges } from "@dnd-kit/modifiers"
import { ScrollArea } from "@/components/ui/scroll-area"

interface LeaderboardSignal {
  symbol: string
  fullSymbol: string
  signalCount: number
  price: number
  change: number
  volume: number
  quoteVolume: number
  high24h: number
  low24h: number
  equityCurve: number[]
  logoUrl?: string
}

type SortField = 'symbol' | 'signalCount' | 'price' | 'change' | 'volume'
type SortDirection = 'asc' | 'desc'

interface FloatingSignalWidgetProps {
  pos: { x: number; y: number }
  minimized: boolean
  onToggleMinimize: () => void
  onClose: () => void
}

function FloatingSignalWidget({
  pos,
  minimized,
  onToggleMinimize,
  onClose,
}: FloatingSignalWidgetProps) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: "signal-leaderboard-drag",
  })

  const [signals, setSignals] = useState<LeaderboardSignal[]>([])
  const [loading, setLoading] = useState(true)
  const [sortField, setSortField] = useState<SortField>('signalCount')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const router = useRouter()
  const { getIcon: getTokenIcon } = useTokenIcons()

  const handleTokenClick = (fullSymbol: string) => {
    router.push(`/terminal/futures/${fullSymbol}`)
  }

  // Get live prices via WebSocket
  const symbols = signals.map(s => s.fullSymbol)
  const { prices: livePrices } = useCryptoWebSocket({ 
    symbols, 
    enabled: symbols.length > 0 
  })

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const response = await authFetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/api/market/most-traded`)
        const result = await response.json()
        
        if (result.most_traded) {
          setSignals(prev => {
            return result.most_traded.map((newSignal: LeaderboardSignal) => {
              const existing = prev.find(s => s.symbol === newSignal.symbol)
              return {
                ...newSignal,
                equityCurve: existing?.equityCurve || []
              }
            })
          })
          
          // Lazy load equity curves for signals that don't have them
          result.most_traded.forEach(async (signal: LeaderboardSignal) => {
            try {
              const curveResponse = await fetch(
                `${process.env.NEXT_PUBLIC_BACKEND_URL}/api/market/equity-curve/${signal.symbol}`
              )
              const curveData = await curveResponse.json()
              
              if (curveData.equityCurve) {
                setSignals(prev => 
                  prev.map(s => 
                    s.symbol === signal.symbol && (!s.equityCurve || s.equityCurve.length === 0)
                      ? { ...s, equityCurve: curveData.equityCurve }
                      : s
                  )
                )
              }
            } catch (error) {
              console.error(`Failed to fetch equity curve for ${signal.symbol}:`, error)
            }
          })
        }
      } catch (error) {
        console.error("Failed to fetch Signal Leaderboard:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchLeaderboard()
    const interval = setInterval(fetchLeaderboard, 60 * 1000)
    return () => clearInterval(interval)
  }, [])

  const getCurrentPrice = (fullSymbol: string, cachedPrice: number) => {
    const livePrice = livePrices.get(fullSymbol)
    return livePrice ? livePrice.price : cachedPrice
  }

  const getCurrentChange = (fullSymbol: string, cachedChange: number) => {
    const livePrice = livePrices.get(fullSymbol)
    return livePrice ? livePrice.priceChangePercent : cachedChange
  }

  const handleSort = useCallback((field: SortField) => {
    setSortField(prev => {
      if (prev === field) {
        setSortDirection(d => d === 'asc' ? 'desc' : 'asc')
        return field
      }
      setSortDirection('desc')
      return field
    })
  }, [])

  const sortedSignals = useMemo(() => {
    const sorted = [...signals]
    sorted.sort((a, b) => {
      let cmp = 0
      switch (sortField) {
        case 'symbol':
          cmp = a.symbol.localeCompare(b.symbol)
          break
        case 'signalCount':
          cmp = a.signalCount - b.signalCount
          break
        case 'price':
          cmp = getCurrentPrice(a.fullSymbol, a.price) - getCurrentPrice(b.fullSymbol, b.price)
          break
        case 'change':
          cmp = getCurrentChange(a.fullSymbol, a.change) - getCurrentChange(b.fullSymbol, b.change)
          break
        case 'volume':
          cmp = a.quoteVolume - b.quoteVolume
          break
      }
      return sortDirection === 'asc' ? cmp : -cmp
    })
    return sorted
  }, [signals, sortField, sortDirection, livePrices])

  const formatVolume = (volume: number) => {
    if (volume >= 1e9) return `$${(volume / 1e9).toFixed(2)}B`
    if (volume >= 1e6) return `$${(volume / 1e6).toFixed(2)}M`
    if (volume >= 1e3) return `$${(volume / 1e3).toFixed(2)}K`
    return `$${volume.toFixed(2)}`
  }

  const SortHeader = ({ label, sortKeyName, className }: { label: string; sortKeyName: SortField; className?: string }) => (
    <button
      onClick={() => handleSort(sortKeyName)}
      className={cn(
        "flex items-center gap-0.5 text-[11px] text-muted-foreground hover:text-foreground transition-colors group",
        className
      )}
    >
      <span>{label}</span>
      {sortField === sortKeyName ? (
        sortDirection === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
      ) : (
        <ArrowUpDown className="h-3 w-3 opacity-0 group-hover:opacity-50 transition-opacity" />
      )}
    </button>
  )

  const x = pos.x + (transform?.x ?? 0)
  const y = pos.y + (transform?.y ?? 0)

  return (
    <div
      style={{
        position: "fixed",
        left: x,
        top: y,
        width: 380,
        zIndex: 50,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{
          opacity: 1,
          y: 0,
          height: minimized ? 40 : "calc(100dvh - 7rem)",
        }}
        transition={{
          opacity: { duration: 0.12 },
          y: { duration: 0.12 },
          height: { type: "spring", stiffness: 380, damping: 36 },
        }}
        className="flex flex-col overflow-hidden rounded-lg border border-border bg-background shadow-2xl"
      >
        {/* ── Header bar ──────────────────────────────────────────────────── */}
        <div className="flex items-stretch h-10 flex-shrink-0 border-b border-border/50 bg-background">
          <div
            ref={setNodeRef}
            {...listeners}
            {...attributes}
            className="w-6 flex items-center justify-center cursor-grab active:cursor-grabbing hover:bg-muted/50 flex-shrink-0 transition-colors border-r border-border/30"
            title="Drag to reposition"
          >
            <svg width="8" height="16" viewBox="0 0 8 16" fill="none">
              <circle cx="2" cy="3.5" r="1.1" fill="currentColor" className="text-muted-foreground/40" />
              <circle cx="6" cy="3.5" r="1.1" fill="currentColor" className="text-muted-foreground/40" />
              <circle cx="2" cy="8"   r="1.1" fill="currentColor" className="text-muted-foreground/40" />
              <circle cx="6" cy="8"   r="1.1" fill="currentColor" className="text-muted-foreground/40" />
              <circle cx="2" cy="12.5" r="1.1" fill="currentColor" className="text-muted-foreground/40" />
              <circle cx="6" cy="12.5" r="1.1" fill="currentColor" className="text-muted-foreground/40" />
            </svg>
          </div>

          <div className="flex-1 flex items-center px-2.5 gap-2 min-w-0">
            <Activity className="h-3.5 w-3.5 text-primary flex-shrink-0" />
            <span className="uppercase tracking-widest text-[11px] font-semibold text-muted-foreground">
              TOP SIGNALS
            </span>
          </div>

          <div className="flex items-center pr-1 gap-0.5 flex-shrink-0">
            <button
              onClick={onToggleMinimize}
              className="h-7 w-7 flex items-center justify-center rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              aria-label={minimized ? "Expand" : "Minimize"}
            >
              {minimized ? (
                <ChevronUp className="h-3.5 w-3.5" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5" />
              )}
            </button>
            <button
              onClick={onClose}
              className="h-7 w-7 flex items-center justify-center rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Close"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* ── Content (collapsed when minimized) ───────────────────────── */}
        {!minimized && (
          <div className="flex flex-col flex-1 overflow-hidden min-h-0 bg-background">
            {/* Column headers */}
            <div className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-2 px-3 py-1.5 border-b border-border flex-shrink-0">
              <SortHeader label="Name / Vol" sortKeyName="symbol" />
              <SortHeader label="Signals" sortKeyName="signalCount" className="justify-end min-w-[56px] text-right" />
              <SortHeader label="Last Price" sortKeyName="price" className="justify-end min-w-[90px] text-right" />
              <SortHeader label="24h chg%" sortKeyName="change" className="justify-end min-w-[80px] text-right" />
            </div>

            <ScrollArea className="flex-1 min-h-0">
              <div className="divide-y divide-border">
                {loading ? (
                  <div className="divide-y divide-border">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="flex items-center justify-between px-3 py-2.5 animate-pulse">
                        <div className="flex items-center gap-2.5">
                          <div className="h-8 w-8 bg-muted rounded-full" />
                          <div className="space-y-1">
                            <div className="h-3.5 w-16 bg-muted rounded" />
                            <div className="h-2.5 w-14 bg-muted rounded" />
                          </div>
                        </div>
                        <div className="text-right space-y-1">
                          <div className="h-3.5 w-16 bg-muted rounded ml-auto" />
                          <div className="h-2.5 w-12 bg-muted rounded ml-auto" />
                        </div>
                        <div className="h-6 w-16 bg-muted rounded-md ml-3" />
                      </div>
                    ))}
                  </div>
                ) : (
                  sortedSignals.map((signal) => {
                    const currentPrice = getCurrentPrice(signal.fullSymbol, signal.price)
                    const currentChange = getCurrentChange(signal.fullSymbol, signal.change)
                    const isPositive = currentChange >= 0

                    return (
                      <div 
                        key={signal.symbol}
                        className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-2 px-3 py-2.5 hover:bg-muted/50 transition-colors cursor-pointer"
                        onClick={() => handleTokenClick(signal.fullSymbol)}
                      >
                        {/* Name + Volume */}
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="relative w-8 h-8 rounded-full overflow-hidden flex-shrink-0 bg-muted flex items-center justify-center">
                            {(() => {
                              const backendLogo = signal.logoUrl
                                ? `${process.env.NEXT_PUBLIC_BACKEND_URL}/api/market/proxy-image?url=${encodeURIComponent(signal.logoUrl)}`
                                : null
                              const geckoLogo = getTokenIcon(signal.symbol)
                              const logoSrc = backendLogo || geckoLogo

                              return logoSrc ? (
                                <img
                                  src={logoSrc}
                                  alt={signal.symbol}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    if (backendLogo && geckoLogo && e.currentTarget.src !== geckoLogo) {
                                      e.currentTarget.src = geckoLogo
                                    } else {
                                      e.currentTarget.style.display = 'none'
                                      const fallback = e.currentTarget.nextElementSibling as HTMLElement
                                      if (fallback) fallback.style.display = 'flex'
                                    }
                                  }}
                                />
                              ) : null
                            })()}
                            <span
                              className="w-full h-full bg-muted flex items-center justify-center text-[10px] font-bold text-muted-foreground"
                              style={{ display: (signal.logoUrl || getTokenIcon(signal.symbol)) ? 'none' : 'flex' }}
                            >
                              {signal.symbol.substring(0, 2)}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{signal.symbol}</p>
                            <p className="text-[11px] text-muted-foreground">
                              {formatVolume(signal.quoteVolume)}
                            </p>
                          </div>
                        </div>

                        {/* Signal Count Badge */}
                        <div className="min-w-[56px] flex justify-end">
                          <span className="text-xs font-medium px-2 py-0.5 bg-primary/10 text-primary rounded-full tabular-nums">
                            {signal.signalCount}
                          </span>
                        </div>

                        {/* Last Price */}
                        <div className="text-right min-w-[90px]">
                          <p className="text-sm font-medium font-mono">
                            <span className="mb-1 inline-block">
                              <NumberFlow
                                value={currentPrice}
                                format={{
                                  minimumFractionDigits: currentPrice < 1 ? 4 : 2,
                                  maximumFractionDigits: currentPrice < 1 ? 6 : 2,
                                }}
                              />
                            </span>
                          </p>
                          <p className="text-[11px] text-muted-foreground font-mono">
                            $<span className="mb-1 inline-block">
                              <NumberFlow
                                value={currentPrice}
                                format={{
                                  minimumFractionDigits: currentPrice < 1 ? 4 : 2,
                                  maximumFractionDigits: currentPrice < 1 ? 6 : 2,
                                }}
                              />
                            </span>
                          </p>
                        </div>

                        {/* 24h Change Badge */}
                        <div className="min-w-[80px] flex justify-end">
                          <span className={cn(
                            "inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold font-mono",
                            isPositive
                              ? "bg-green-500/15 text-green-500"
                              : "bg-red-500/15 text-red-500"
                          )}>
                            {isPositive ? "+" : ""}
                            <span className="mb-1 inline-block">
                              <NumberFlow
                                value={currentChange}
                                format={{
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }}
                                suffix="%"
                              />
                            </span>
                          </span>
                        </div>
                      </div>
                    )
                  })
                )}

                {sortedSignals.length === 0 && !loading && (
                  <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                    No signals found
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        )}
      </motion.div>
    </div>
  )
}

interface SignalLeaderboardProps {
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export function SignalLeaderboard({ open: propOpen, onOpenChange: propOnOpenChange }: SignalLeaderboardProps = {}) {
  const storeOpen = useCommandStore((s) => s.isSignalLeaderboardOpen)
  const setStoreOpen = useCommandStore((s) => s.setSignalLeaderboardOpen)
  
  const open = propOpen !== undefined ? propOpen : storeOpen
  const setOpen = propOnOpenChange || setStoreOpen

  const [minimized, setMinimized] = useState(false)
  const [pos, setPos] = useState({ x: 20, y: 80 })
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (open) setMinimized(false)
  }, [open])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  )

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setPos((prev) => ({
      x: Math.max(0, prev.x + event.delta.x),
      y: Math.max(0, prev.y + event.delta.y),
    }))
  }, [])

  if (!mounted) return null

  return (
    <DndContext
      sensors={sensors}
      modifiers={[restrictToWindowEdges]}
      onDragEnd={handleDragEnd}
    >
      {open &&
        createPortal(
          <FloatingSignalWidget
            pos={pos}
            minimized={minimized}
            onToggleMinimize={() => setMinimized((m) => !m)}
            onClose={() => setOpen(false)}
          />,
          document.body
        )}
    </DndContext>
  )
}
